import {
  Banknote,
  ClipboardCheck,
  CreditCard,
  FileSpreadsheet,
  FileText,
  History,
  LoaderCircle,
  Network,
  Receipt,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import {
  billingReportCsvUrl,
  createBillingPayNowUrl,
  evaluateBillingPolicy,
  getBillingPolicyFramework,
  getBillingInvoice,
  getBillingReports,
  listBillingInvoices,
  mutateBillingPolicy,
  transitionBillingInvoice,
  type BillingPolicyFramework,
  type BillingDetail,
  type BillingInvoiceRecord,
  type BillingMetrics,
  type BillingPermissions,
} from "../../lib/arRecoveryBillingApi";

type BillingTab = "summary" | "policy" | "lines" | "stripe" | "payments" | "receipts" | "history" | "reporting";

const EMPTY_METRICS: BillingMetrics = {
  invoiceCount: 0,
  originalTotalCents: 0,
  amountPaidCents: 0,
  remainingBalanceCents: 0,
  draftInvoices: 0,
  awaitingApproval: 0,
  approvedInvoices: 0,
  sentInvoices: 0,
  overdueInvoices: 0,
  paymentFailures: 0,
  paidInvoices: 0,
  partialPayments: 0,
  creditsCents: 0,
  voidedInvoices: 0,
  stripeExceptions: 0,
};

const EMPTY_PERMISSIONS: BillingPermissions = {
  viewQueue: false,
  finalizeInvoice: false,
  submitApproval: false,
  approveInvoice: false,
  deliverInvoice: false,
  managePayment: false,
  manageStripe: false,
  correctInvoice: false,
  exportReport: false,
  clientBilling: false,
};

const tabs: Array<{ key: BillingTab; label: string; icon: typeof FileText }> = [
  { key: "summary", label: "Summary", icon: Banknote },
  { key: "policy", label: "Policy", icon: Network },
  { key: "lines", label: "Fee Lines", icon: FileText },
  { key: "stripe", label: "Stripe", icon: CreditCard },
  { key: "payments", label: "Payments", icon: ClipboardCheck },
  { key: "receipts", label: "Receipts", icon: Receipt },
  { key: "history", label: "History", icon: History },
  { key: "reporting", label: "Reporting", icon: FileSpreadsheet },
];

export function ARRecoveryBillingCenter() {
  const [invoices, setInvoices] = useState<BillingInvoiceRecord[]>([]);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [selected, setSelected] = useState<BillingDetail | null>(null);
  const [reports, setReports] = useState<Awaited<ReturnType<typeof getBillingReports>> | null>(null);
  const [policyFramework, setPolicyFramework] = useState<BillingPolicyFramework | null>(null);
  const [search, setSearch] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [sort, setSort] = useState("updated-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageCount, setPageCount] = useState(1);
  const [tab, setTab] = useState<BillingTab>("summary");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function refreshQueue(preferredInvoiceId?: string) {
    setLoading(true);
    setError("");
    try {
      const result = await listBillingInvoices({ search: search.trim(), invoiceStatus, paymentStatus, sort, page, pageSize });
      setInvoices(result.invoices);
      setMetrics(result.metrics);
      setPermissions(result.permissions);
      setPageCount(Math.max(1, result.pagination.pageCount));
      const nextId = preferredInvoiceId || selected?.invoice.billingInvoiceId || result.invoices[0]?.billingInvoiceId;
      if (nextId) await selectInvoice(nextId, false);
      else setSelected(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Billing queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  async function selectInvoice(billingInvoiceId: string, showErrors = true) {
    try {
      const detail = await getBillingInvoice(billingInvoiceId);
      setSelected(detail);
      setPermissions(detail.permissions);
    } catch (cause) {
      if (showErrors) setError(cause instanceof Error ? cause.message : "Billing invoice detail could not be loaded.");
    }
  }

  async function runMutation(action: () => Promise<unknown>, success: string) {
    setWorking(true);
    setMessage("");
    setError("");
    try {
      await action();
      setMessage(success);
      await refreshQueue(selected?.invoice.billingInvoiceId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Billing action could not be completed.");
    } finally {
      setWorking(false);
    }
  }

  useEffect(() => {
    void refreshQueue();
    // Queue refresh is intentionally driven by committed controls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, invoiceStatus, paymentStatus, sort]);

  async function loadTab(nextTab: BillingTab) {
    setTab(nextTab);
    if (nextTab === "reporting") setReports(await getBillingReports());
    if (nextTab === "policy") setPolicyFramework(await getBillingPolicyFramework());
  }

  const invoice = selected?.invoice;

  return (
    <section className="billing-center-enterprise mx-auto grid w-full max-w-[100rem] gap-4" aria-label="Billing Center">
      <header className="overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_18px_44px_rgba(10,37,64,.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[.18em] text-[#14B8A6]">Canonical Client Billing</p>
            <h2 className="mt-1 font-display text-3xl font-bold tracking-[-.03em] text-[#0A2540]">Billing Center</h2>
            <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-[#475569]">
              Manage CollabRecover invoices issued to clients from approved Settlement records, Stripe-hosted Pay Now links, receipts, and billing history.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="billing-button billing-button-secondary" type="button" onClick={() => void refreshQueue()} disabled={loading}>
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              Refresh
            </button>
            <a className="billing-button billing-button-secondary" href={billingReportCsvUrl()} download>
              <FileSpreadsheet className="size-4" aria-hidden="true" />
              Export CSV
            </a>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-[#BFEAFF] sm:grid-cols-3 xl:grid-cols-6">
          <BillingMetric label="Invoices" value={metrics.invoiceCount.toLocaleString()} note={money(metrics.originalTotalCents)} />
          <BillingMetric label="Outstanding" value={money(metrics.remainingBalanceCents)} note={`${metrics.overdueInvoices} overdue`} />
          <BillingMetric label="Paid" value={money(metrics.amountPaidCents)} note={`${metrics.paidInvoices} paid`} />
          <BillingMetric label="Awaiting Approval" value={metrics.awaitingApproval.toLocaleString()} note="Controlled duty split" />
          <BillingMetric label="Partial" value={metrics.partialPayments.toLocaleString()} note="Partial payments" />
          <BillingMetric label="Stripe Exceptions" value={metrics.stripeExceptions.toLocaleString()} note="Provider sync" />
        </div>
      </header>

      {message ? <Status tone="success">{message}</Status> : null}
      {error ? <Status tone="error">{error}</Status> : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(21rem,24rem)_minmax(0,1fr)]">
        <aside className="overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_18px_44px_rgba(10,37,64,.08)]">
          <div className="border-b border-[#BFEAFF] p-4">
            <div className="flex items-center gap-2 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
              <Search className="size-4 text-[#14B8A6]" aria-hidden="true" />
              <input className="min-w-0 flex-1 bg-transparent text-sm font-bold text-[#0A2540] outline-none" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && refreshQueue()} placeholder="Search invoice, client, settlement..." />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <select className="billing-input" value={invoiceStatus} onChange={(event) => { setInvoiceStatus(event.target.value); setPage(1); }}>
                <option value="">All statuses</option>
                {["DRAFT", "AWAITING_APPROVAL", "APPROVED", "READY_TO_SEND", "SENT", "VIEWED", "OVERDUE", "DISPUTED", "CORRECTED", "CREDITED", "VOIDED"].map((status) => <option key={status} value={status}>{label(status)}</option>)}
              </select>
              <select className="billing-input" value={paymentStatus} onChange={(event) => { setPaymentStatus(event.target.value); setPage(1); }}>
                <option value="">All payments</option>
                {["UNPAID", "PAYMENT_PENDING", "PARTIALLY_PAID", "PAID", "PAYMENT_FAILED", "REFUNDED", "DISPUTED", "VOIDED"].map((status) => <option key={status} value={status}>{label(status)}</option>)}
              </select>
              <select className="billing-input" value={sort} onChange={(event) => setSort(event.target.value)}>
                <option value="updated-desc">Recently updated</option>
                <option value="due-asc">Due soon</option>
                <option value="due-desc">Due latest</option>
                <option value="amount-desc">Largest balance</option>
                <option value="status-asc">Status</option>
              </select>
              <select className="billing-input" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                {[25, 50, 100].map((size) => <option key={size} value={size}>{size} rows</option>)}
              </select>
            </div>
          </div>
          <div className="max-h-[48rem] space-y-2 overflow-y-auto p-3 overscroll-contain">
            {loading ? <div className="flex items-center gap-2 p-4 text-sm font-bold text-[#475569]"><LoaderCircle className="size-4 animate-spin" /> Loading billing queue...</div> : null}
            {!loading && invoices.length === 0 ? <p className="p-4 text-sm font-bold text-[#475569]">No billing invoices match the current filters.</p> : null}
            {invoices.map((item) => (
              <button key={item.billingInvoiceId} type="button" className={`billing-queue-card ${item.billingInvoiceId === invoice?.billingInvoiceId ? "is-active" : ""}`} onClick={() => selectInvoice(item.billingInvoiceId)}>
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-[#0A2540]">{item.invoiceNumber}</span>
                    <span className="mt-1 block truncate text-xs font-extrabold text-[#475569]">{item.clientName || item.clientId}</span>
                  </span>
                  <span className="rounded-full bg-[#F4FBFF] px-2 py-1 text-xs font-black text-[#0A2540]">{money(item.remainingBalanceCents)}</span>
                </span>
                <span className="mt-3 grid grid-cols-2 gap-2 text-left text-[11px] font-black uppercase tracking-[.12em] text-[#64748B]">
                  <span>{label(item.invoiceStatus)}</span>
                  <span>{label(item.paymentStatus)}</span>
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-[#BFEAFF] px-4 py-3 text-xs font-black text-[#0A2540]">
            <button className="billing-page-button" type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
            Page {page} of {pageCount}
            <button className="billing-page-button" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))}>Next</button>
          </div>
        </aside>

        <main className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_18px_44px_rgba(10,37,64,.08)]">
          {!invoice ? (
            <div className="p-8 text-sm font-bold text-[#475569]">Select a billing invoice to review ownership, payment status, Stripe synchronization, receipts, and history.</div>
          ) : (
            <>
              <div className="border-b border-[#BFEAFF] p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-[11px] font-black uppercase tracking-[.18em] text-[#14B8A6]">{invoice.clientName || invoice.clientId}</p>
                    <h3 className="mt-1 font-display text-2xl font-bold tracking-[-.03em] text-[#0A2540]">{invoice.invoiceNumber}</h3>
                    <p className="mt-1 text-sm font-bold text-[#475569]">Settlement {invoice.settlementNumber || invoice.settlementId} · Due {formatDate(invoice.dueDate)} · {money(invoice.remainingBalanceCents)} remaining</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <ActionButton disabled={!permissions.submitApproval || working} onClick={() => runTransition("submit_approval", "Invoice submitted for approval.")}>Submit</ActionButton>
                    <ActionButton disabled={!permissions.approveInvoice || working} onClick={() => runTransition("approve", "Invoice approved.")}>Approve</ActionButton>
                    <ActionButton disabled={!permissions.deliverInvoice || working} onClick={() => runTransition("finalize_delivery", "Delivery approved.")}>Delivery</ActionButton>
                    <ActionButton disabled={!permissions.deliverInvoice || working} onClick={() => runTransition("mark_sent", "Invoice marked sent.")}><Send className="size-4" /> Sent</ActionButton>
                    <ActionButton disabled={!permissions.manageStripe || working || invoice.remainingBalanceCents <= 0} onClick={() => runMutation(() => createBillingPayNowUrl(invoice.billingInvoiceId), "Pay Now URL prepared or reused.")}>Pay Now URL</ActionButton>
                    <ActionButton disabled={!permissions.correctInvoice || working} onClick={() => runTransition("record_reminder", "Billing reminder recorded.")}>Reminder</ActionButton>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
                  <BillingFact label="Invoice status" value={label(invoice.invoiceStatus)} />
                  <BillingFact label="Payment status" value={label(invoice.paymentStatus)} />
                  <BillingFact label="Stripe status" value={label(invoice.stripeStatus)} />
                  <BillingFact label="Original total" value={money(invoice.originalTotalCents)} />
                  <BillingFact label="Paid" value={money(invoice.amountPaidCents)} />
                </div>
              </div>

              <nav className="flex gap-2 overflow-x-auto border-b border-[#BFEAFF] bg-[#F8FDFF] px-4 py-3">
                {tabs.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button key={item.key} type="button" className={`billing-tab ${tab === item.key ? "is-active" : ""}`} onClick={() => loadTab(item.key)}>
                      <Icon className="size-4" aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
              </nav>

              <div className="p-5">
                {tab === "summary" ? <Summary invoice={invoice} /> : null}
                {tab === "policy" ? <PolicyFramework framework={policyFramework} selectedInvoice={invoice} onRefresh={() => loadTab("policy")} /> : null}
                {tab === "lines" ? <Lines invoice={invoice} /> : null}
                {tab === "stripe" ? <StripePanel selected={selected} /> : null}
                {tab === "payments" ? <RecordList title="Payment History" rows={selected.payments} empty="No billing payments recorded." /> : null}
                {tab === "receipts" ? <RecordList title="Receipt History" rows={selected.receipts} empty="No receipts recorded." /> : null}
                {tab === "history" ? <RecordList title="Billing History" rows={selected.history} empty="No billing history recorded." /> : null}
                {tab === "reporting" ? <Reports reports={reports} /> : null}
              </div>
            </>
          )}
        </main>
      </div>
      <style>{billingStyles}</style>
    </section>
  );

  function runTransition(action: Parameters<typeof transitionBillingInvoice>[1]["action"], success: string) {
    if (!invoice) return Promise.resolve();
    return runMutation(
      () => transitionBillingInvoice(invoice.billingInvoiceId, { action, reason: `${success} Preview certification action.`, expectedVersion: invoice.version }),
      success,
    );
  }
}

function Summary({ invoice }: { invoice: BillingInvoiceRecord }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <BillingPanel title="Canonical Ownership">
        <Field label="Company" value={invoice.companyId} />
        <Field label="Client" value={invoice.clientName || invoice.clientId} />
        <Field label="Settlement" value={invoice.settlementNumber || invoice.settlementId} />
        <Field label="Invoice Draft" value={invoice.invoiceDraftId} />
      </BillingPanel>
      <BillingPanel title="Billing Contact">
        {Object.keys(invoice.billingContact || {}).length ? Object.entries(invoice.billingContact).slice(0, 6).map(([key, value]) => <Field key={key} label={label(key)} value={String(value ?? "")} />) : <p className="text-sm font-bold text-[#475569]">No billing contact metadata configured.</p>}
      </BillingPanel>
      <BillingPanel title="Terms and Aging">
        <Field label="Invoice date" value={formatDate(invoice.invoiceDate)} />
        <Field label="Due date" value={formatDate(invoice.dueDate)} />
        <Field label="Terms" value={invoice.paymentTerms} />
        <Field label="Exception" value={label(invoice.exceptionStatus)} />
      </BillingPanel>
    </div>
  );
}

function Lines({ invoice }: { invoice: BillingInvoiceRecord }) {
  return (
    <BillingPanel title="Approved Recovery-Fee Lines">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">
            <tr><th className="py-2 pr-3">Description</th><th className="py-2 pr-3">Basis</th><th className="py-2 pr-3 text-right">Amount</th></tr>
          </thead>
          <tbody className="divide-y divide-[#D8F3FF] font-bold text-[#0A2540]">
            {invoice.lineItems.map((line, index) => (
              <tr key={`${line.lineId || index}`}>
                <td className="py-3 pr-3">{String(line.description || line.lineType || "Recovery fee")}</td>
                <td className="py-3 pr-3">{String(line.settlementLineId || line.basis || "Approved settlement")}</td>
                <td className="py-3 pr-3 text-right">{money(Number(line.amountCents || 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </BillingPanel>
  );
}

function PolicyFramework({ framework, selectedInvoice, onRefresh }: { framework: BillingPolicyFramework | null; selectedInvoice: BillingInvoiceRecord; onRefresh: () => Promise<void> }) {
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [policyView, setPolicyView] = useState<"overview" | "policies" | "editor" | "lifecycle" | "versions" | "templates" | "risk" | "holds" | "reminders" | "analytics">("overview");
  const [policySearch, setPolicySearch] = useState("");
  const [policyStatusFilter, setPolicyStatusFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [sortMode, setSortMode] = useState("updated-desc");
  if (!framework) return <p className="text-sm font-bold text-[#475569]">Loading Billing Policy Framework...</p>;
  const activePolicy = framework.policies.find((policy) => policy.clientId === selectedInvoice.clientId)
    || framework.policies[0]
    || null;
  const template = activePolicy?.templateId
    ? framework.templates.find((item) => item.templateId === activePolicy.templateId)
    : framework.templates.find((item) => item.templateKey === "collabrecover-standard");
  const policySource = activePolicy || template;
  const risk = framework.riskProfiles.find((item) => item.clientId === selectedInvoice.clientId);
  const evaluations = framework.evaluations.filter((item) => item.clientId === selectedInvoice.clientId);
  const policyMetrics = summarizePolicies(framework);
  const riskByClient = new Map(framework.riskProfiles.map((profile) => [profile.clientId, profile]));
  const activeClientIds = new Set(framework.policies.filter((policy) => policy.status === "ACTIVE").map((policy) => policy.clientId));
  const filteredPolicies = sortPolicies(framework.policies.filter((policy) => {
    const riskLevel = riskByClient.get(policy.clientId)?.riskLevel || String(policy.riskRules.defaultLevel || "LOW");
    const haystack = [policy.policyName, policy.policyId, policy.clientName, policy.companyId, policy.templateName, policy.status, riskLevel]
      .join(" ")
      .toLowerCase();
    if (policySearch && !haystack.includes(policySearch.toLowerCase())) return false;
    if (policyStatusFilter && policy.status !== policyStatusFilter) return false;
    if (riskFilter && riskLevel !== riskFilter) return false;
    return true;
  }), sortMode);
  const selectedClientHolds = framework.serviceHolds.filter((item) => String(item.clientId) === selectedInvoice.clientId);
  const selectedClientReminders = framework.reminderSchedules.filter((item) => String(item.clientId) === selectedInvoice.clientId);
  const policyViews: Array<{ key: typeof policyView; label: string }> = [
    { key: "overview", label: "Overview" },
    { key: "policies", label: "Policy List" },
    { key: "editor", label: "Editor" },
    { key: "lifecycle", label: "Lifecycle" },
    { key: "versions", label: "Versions" },
    { key: "templates", label: "Templates" },
    { key: "risk", label: "Risk" },
    { key: "holds", label: "Service Holds" },
    { key: "reminders", label: "Reminders" },
    { key: "analytics", label: "Analytics" },
  ];
  return (
    <div className="grid gap-4">
      {message ? <Status tone="success">{message}</Status> : null}
      <BillingPanel title="Policy Operations Command">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
          <Field label="Active" value={String(policyMetrics.active)} />
          <Field label="Draft" value={String(policyMetrics.draft)} />
          <Field label="Scheduled" value={String(policyMetrics.scheduled)} />
          <Field label="High risk" value={String(policyMetrics.highRisk)} />
          <Field label="Approval" value={String(policyMetrics.requiresApproval)} />
          <Field label="No active policy" value={String(framework.clients.filter((client) => !activeClientIds.has(client.clientId)).length)} />
          <Field label="Expiring soon" value={String(policyMetrics.expiringSoon)} />
          <Field label="Hold review" value={String(policyMetrics.serviceHoldsPendingReview)} />
        </div>
      </BillingPanel>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Billing Policy workspace sections">
        {policyViews.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={policyView === item.key}
            className={`billing-tab ${policyView === item.key ? "is-active" : ""}`}
            onClick={() => setPolicyView(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.1fr_.9fr]">
        <BillingPanel title="Client Billing Policy">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Client" value={selectedInvoice.clientName || selectedInvoice.clientId} />
            <Field label="Policy" value={activePolicy?.policyName || template?.templateName || "CollabRecover Standard"} />
            <Field label="Template" value={activePolicy?.templateName || template?.templateName || "Foundation default"} />
            <Field label="Status" value={activePolicy?.status || template?.status || "Foundation Default"} />
            <Field label="Effective" value={activePolicy?.effectiveFrom || "Configured by template"} />
            <Field label="Version" value={String(activePolicy?.version || template?.version || 1)} />
          </div>
        </BillingPanel>
        <BillingPanel title="Billing Risk Profile">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Risk level" value={risk?.riskLevel || String((policySource?.riskRules as Record<string, unknown> | undefined)?.defaultLevel || "LOW")} />
            <Field label="Payment reliability" value={risk ? `${risk.paymentReliabilityScore}%` : "100%"} />
            <Field label="Service hold" value={risk?.serviceHoldStatus || readable((policySource?.serviceHoldRules as Record<string, unknown> | undefined)?.mode) || "Reminder Only"} />
            <Field label="Approval level" value={risk?.approvalLevel || readable((policySource?.invoiceApprovalRules as Record<string, unknown> | undefined)?.mode) || "Manual"} />
          </div>
        </BillingPanel>
      </div>

      <BillingPanel title="Policy Actions">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <ActionButton disabled={working} onClick={() => void runPreviewEvaluation()}>
            Run Preview Evaluation
          </ActionButton>
          <ActionButton disabled={working} onClick={() => void runPolicyMutation("create_policy", {
            clientId: selectedInvoice.clientId,
            templateId: template?.templateId || null,
            reason: "Preview Billing Policy draft created from the Billing Center.",
            policy: {
              clientId: selectedInvoice.clientId,
              templateId: template?.templateId || null,
              policyName: `${selectedInvoice.clientName || selectedInvoice.clientId} Billing Policy Draft`,
              effectiveFrom: new Date().toISOString().slice(0, 10),
              managerNotes: "Synthetic Preview policy draft. No Stripe charge or Production data.",
            },
          }, "Draft Billing Policy persisted.")}>
            Create Draft Policy
          </ActionButton>
          <ActionButton disabled={working || !activePolicy} onClick={() => void runPolicyMutation("clone_policy", {
            policyId: activePolicy?.policyId,
            reason: "Preview clone created for operational mutation certification.",
          }, "Billing Policy clone persisted.")}>
            Clone Policy
          </ActionButton>
          <ActionButton disabled={working || !activePolicy || activePolicy.status !== "DRAFT"} onClick={() => void runPolicyMutation("submit_for_approval", {
            policyId: activePolicy?.policyId,
            expectedVersion: activePolicy?.version,
            reason: "Submitted for Preview approval certification.",
          }, "Billing Policy submitted for approval.")}>
            Submit Draft
          </ActionButton>
          <ActionButton disabled={working || !activePolicy || activePolicy.status !== "PENDING_APPROVAL"} onClick={() => void runPolicyMutation("approve_policy", {
            policyId: activePolicy?.policyId,
            expectedVersion: activePolicy?.version,
            reason: "Approved for Preview operational certification.",
          }, "Billing Policy approval persisted.")}>
            Approve Policy
          </ActionButton>
          <ActionButton disabled={working || !activePolicy || activePolicy.status !== "PENDING_APPROVAL"} onClick={() => void runPolicyMutation("reject_policy", {
            policyId: activePolicy?.policyId,
            expectedVersion: activePolicy?.version,
            reason: "Rejected during Preview operational certification.",
          }, "Billing Policy rejection persisted.")}>
            Reject Policy
          </ActionButton>
          <ActionButton disabled={working || !activePolicy || !["APPROVED", "SCHEDULED"].includes(activePolicy.status)} onClick={() => void runPolicyMutation("activate_policy", {
            policyId: activePolicy?.policyId,
            expectedVersion: activePolicy?.version,
            replacementPolicyId: framework.policies.find((policy) => policy.clientId === activePolicy?.clientId && policy.status === "ACTIVE")?.policyId || null,
            effectiveFrom: new Date().toISOString().slice(0, 10),
            reason: "Activated through Preview operational certification.",
          }, "Billing Policy activation persisted.")}>
            Activate Policy
          </ActionButton>
          {[
            ["Assign Template", "templates"],
            ["Replace With New Version", "versions"],
            ["View History", "versions"],
            ["Compare Versions", "versions"],
            ["Review Risk Profile", "risk"],
            ["Review Service Hold", "holds"],
            ["Export Policy Summary", "analytics"],
          ].map(([action, view]) => (
            <button key={action} type="button" className="billing-button billing-button-secondary" onClick={() => setPolicyView(view as typeof policyView)}>
              {action}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs font-bold leading-5 text-[#64748B]">
          Active policies remain immutable. Editing creates a governed replacement version; client users cannot mutate policy settings.
        </p>
      </BillingPanel>

      {policyView === "overview" ? (
        <>
          <div className="grid gap-4 lg:grid-cols-3">
            <BillingPanel title="Trigger Rules">
              <RuleList rows={(policySource?.triggerRules || []).map(String)} empty="Manual Billing" />
            </BillingPanel>
            <BillingPanel title="Fee Model">
              <KeyValues value={policySource?.feeModel || { model: "PERCENTAGE_RECOVERY" }} />
            </BillingPanel>
            <BillingPanel title="Payment Terms">
              <KeyValues value={policySource?.paymentTerms || { term: selectedInvoice.paymentTerms }} />
            </BillingPanel>
            <BillingPanel title="AutoPay Rules">
              <KeyValues value={policySource?.autopayRules || { mode: "OPTIONAL", timing: "DUE_DATE" }} />
            </BillingPanel>
            <BillingPanel title="Service Hold Rules">
              <KeyValues value={policySource?.serviceHoldRules || { mode: "REMINDER_ONLY" }} />
            </BillingPanel>
            <BillingPanel title="Reminder Rules">
              <RuleList rows={(policySource?.reminderRules || []).map(String)} empty="No reminders configured" />
            </BillingPanel>
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            <BillingPanel title="Evaluation Snapshots">
              <RecordList title="Recent evaluations" rows={evaluations} empty="No policy evaluations have been recorded for this client yet." />
            </BillingPanel>
            <BillingPanel title="AutoPay, Holds, and Reminders">
              <div className="grid gap-2">
                <Field label="AutoPay records" value={String(framework.autopayConsents.filter((item) => String(item.clientId) === selectedInvoice.clientId).length)} />
                <Field label="Service-hold drafts" value={String(selectedClientHolds.length)} />
                <Field label="Reminder drafts" value={String(selectedClientReminders.length)} />
                <Field label="Audit entries" value={String(framework.audit.filter((item) => String(item.clientId) === selectedInvoice.clientId).length)} />
              </div>
            </BillingPanel>
          </div>
        </>
      ) : null}

      {policyView === "policies" ? (
        <BillingPanel title="Policy List">
          <div className="grid gap-3 lg:grid-cols-4">
            <input className="billing-input" value={policySearch} onChange={(event) => setPolicySearch(event.target.value)} placeholder="Search policy, client, template..." aria-label="Search billing policies" />
            <select className="billing-input" value={policyStatusFilter} onChange={(event) => setPolicyStatusFilter(event.target.value)} aria-label="Filter by policy status">
              <option value="">All statuses</option>
              {Array.from(new Set(framework.policies.map((policy) => policy.status))).map((status) => <option key={status} value={status}>{readable(status)}</option>)}
            </select>
            <select className="billing-input" value={riskFilter} onChange={(event) => setRiskFilter(event.target.value)} aria-label="Filter by risk level">
              <option value="">All risk levels</option>
              {["LOW", "MEDIUM", "HIGH"].map((level) => <option key={level} value={level}>{readable(level)}</option>)}
            </select>
            <select className="billing-input" value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="Sort billing policies">
              <option value="updated-desc">Updated date</option>
              <option value="policy-name">Policy name</option>
              <option value="client">Client</option>
              <option value="effective">Effective date</option>
              <option value="risk">Risk level</option>
              <option value="status">Status</option>
            </select>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-[1100px] w-full text-left text-xs">
              <thead className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">
                <tr>
                  {["Policy", "Client", "Company", "Template", "Status", "Version", "Effective", "Expires", "Trigger", "Fee", "Terms", "AutoPay", "Risk", "Hold", "Approval", "Updated"].map((heading) => <th key={heading} className="py-2 pr-3">{heading}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#D8F3FF] font-bold text-[#0A2540]">
                {filteredPolicies.map((policy) => {
                  const rowRisk = riskByClient.get(policy.clientId);
                  return (
                    <tr key={policy.policyId}>
                      <td className="py-3 pr-3"><span className="block font-black">{policy.policyName}</span><span className="text-[10px] text-[#64748B]">{policy.policyId}</span></td>
                      <td className="py-3 pr-3">{policy.clientName || policy.clientId}</td>
                      <td className="py-3 pr-3">{policy.companyId}</td>
                      <td className="py-3 pr-3">{policy.templateName || "Custom"}</td>
                      <td className="py-3 pr-3">{readable(policy.status)}</td>
                      <td className="py-3 pr-3">{policy.version}</td>
                      <td className="py-3 pr-3">{formatDate(policy.effectiveFrom)}</td>
                      <td className="py-3 pr-3">{formatDate(policy.effectiveTo)}</td>
                      <td className="py-3 pr-3">{policy.triggerRules.map(readable).join(", ") || "Manual"}</td>
                      <td className="py-3 pr-3">{readable(policy.feeModel.model || "Recovery fee")}</td>
                      <td className="py-3 pr-3">{readable(policy.paymentTerms.term || "Policy terms")}</td>
                      <td className="py-3 pr-3">{readable(policy.autopayRules.mode || "Optional")}</td>
                      <td className="py-3 pr-3">{readable(rowRisk?.riskLevel || policy.riskRules.defaultLevel || "LOW")}</td>
                      <td className="py-3 pr-3">{readable(policy.serviceHoldRules.mode || "Review")}</td>
                      <td className="py-3 pr-3">{readable(policy.invoiceApprovalRules.mode || rowRisk?.approvalLevel || "Manual")}</td>
                      <td className="py-3 pr-3">{formatDate(policy.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!filteredPolicies.length ? <p className="mt-4 text-sm font-bold text-[#475569]">No policies match the selected filters.</p> : null}
        </BillingPanel>
      ) : null}

      {policyView === "editor" ? <PolicyEditorFoundation framework={framework} activePolicy={activePolicy} /> : null}
      {policyView === "lifecycle" ? <PolicyLifecycleFoundation framework={framework} activePolicy={activePolicy} /> : null}
      {policyView === "versions" ? <PolicyVersionFoundation framework={framework} activePolicy={activePolicy} /> : null}
      {policyView === "templates" ? <TemplateManagementFoundation framework={framework} /> : null}
      {policyView === "risk" ? <RiskManagementFoundation framework={framework} selectedInvoice={selectedInvoice} /> : null}
      {policyView === "holds" ? <ServiceHoldFoundation framework={framework} selectedInvoice={selectedInvoice} /> : null}
      {policyView === "reminders" ? <ReminderManagementFoundation framework={framework} selectedInvoice={selectedInvoice} /> : null}
      {policyView === "analytics" ? <BillingAnalyticsFoundation framework={framework} /> : null}
    </div>
  );

  async function runPreviewEvaluation() {
    setWorking(true);
    setMessage("");
    try {
      await evaluateBillingPolicy({
        clientId: selectedInvoice.clientId,
        sourceEntityType: "VERIFIED_PAYMENT",
        sourceEntityId: `preview-policy-eval-${selectedInvoice.billingInvoiceId}`,
        recoveryAmountCents: selectedInvoice.originalTotalCents,
        contractFeePercent: 25,
        invoiceIssueDate: selectedInvoice.invoiceDate,
        autopayEnrollmentStatus: "NOT_ENROLLED",
        paymentMethodStatus: "NOT_SELECTED",
      });
      setMessage("Preview policy evaluation recorded without creating a payment or invoice.");
      await onRefresh();
    } finally {
      setWorking(false);
    }
  }

  async function runPolicyMutation(action: Parameters<typeof mutateBillingPolicy>[0]["action"], input: Omit<Parameters<typeof mutateBillingPolicy>[0], "action">, success: string) {
    setWorking(true);
    setMessage("");
    try {
      await mutateBillingPolicy({ action, ...input });
      setMessage(success);
      await onRefresh();
    } finally {
      setWorking(false);
    }
  }
}

function PolicyEditorFoundation({ framework, activePolicy }: { framework: BillingPolicyFramework; activePolicy: BillingPolicyFramework["policies"][number] | null }) {
  const selectedTemplate = framework.templates.find((template) => template.templateId === activePolicy?.templateId) || framework.templates[0];
  return (
    <div className="grid gap-4 xl:grid-cols-[.8fr_1.2fr]">
      <BillingPanel title="Policy Editor Foundation">
        <div className="grid gap-3">
          {[
            "Policy Identity",
            "Client Assignment",
            "Billing Trigger",
            "Fee Model",
            "Payment Terms",
            "Invoice Approval",
            "AutoPay",
            "Reminder Schedule",
            "Service-Hold Rules",
            "Risk Rules",
            "Billing Contacts",
            "Client Visibility",
            "Effective Dates",
            "Review and Approval",
          ].map((step, index) => (
            <div key={step} className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-3">
              <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Step {index + 1}</p>
              <p className="text-sm font-black text-[#0A2540]">{step}</p>
            </div>
          ))}
        </div>
      </BillingPanel>
      <BillingPanel title="Current Policy Draft Model">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Policy name" value={activePolicy?.policyName || selectedTemplate?.templateName || "New policy"} />
          <Field label="Source template" value={selectedTemplate?.templateName || "Custom"} />
          <Field label="Policy owner" value="Billing Manager" />
          <Field label="Status" value={activePolicy?.status === "ACTIVE" ? "Active policies require replacement version" : readable(activePolicy?.status || "Draft")} />
          <Field label="Version" value={String(activePolicy?.version || 1)} />
          <Field label="Change reason" value="Required before approval or activation" />
          <Field label="Billing scope" value="Client billing scope, company scoped" />
          <Field label="Contract reference" value="Stored with policy notes and audit" />
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          <ConfigBlock title="Allowed triggers" values={framework.availableRules.triggerRules || []} />
          <ConfigBlock title="Allowed fee models" values={framework.availableRules.feeModels || []} />
          <ConfigBlock title="Allowed payment terms" values={framework.availableRules.paymentTerms || []} />
          <ConfigBlock title="Allowed AutoPay modes" values={framework.availableRules.autopayModes || []} />
          <ConfigBlock title="Allowed service holds" values={framework.availableRules.serviceHoldModes || []} />
          <ConfigBlock title="Allowed risk levels" values={framework.availableRules.riskLevels || []} />
        </div>
        <p className="mt-4 text-xs font-bold leading-5 text-[#64748B]">
          The editor is wired to the protected Billing Policy API model. Active policy edits create replacement versions; client assignment remains company-scoped to prevent cross-tenant policy assignment.
        </p>
      </BillingPanel>
    </div>
  );
}

function PolicyLifecycleFoundation({ framework, activePolicy }: { framework: BillingPolicyFramework; activePolicy: BillingPolicyFramework["policies"][number] | null }) {
  const statuses = ["DRAFT", "PENDING_APPROVAL", "APPROVED", "SCHEDULED", "ACTIVE", "SUPERSEDED", "INACTIVE", "EXPIRED", "REJECTED"];
  return (
    <div className="grid gap-4 lg:grid-cols-[.8fr_1.2fr]">
      <BillingPanel title="Lifecycle Controls">
        <div className="grid gap-2">
          {statuses.map((status, index) => (
            <div key={status} className={`rounded-2xl border p-3 ${activePolicy?.status === status ? "border-[#14B8A6] bg-[#E8FFFB]" : "border-[#D8F3FF] bg-[#F8FDFF]"}`}>
              <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Gate {index + 1}</p>
              <p className="text-sm font-black text-[#0A2540]">{readable(status)}</p>
              <p className="mt-1 text-xs font-bold text-[#64748B]">{lifecycleRule(status)}</p>
            </div>
          ))}
        </div>
      </BillingPanel>
      <BillingPanel title="Lifecycle Readiness">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Only one active policy" value={framework.policies.filter((policy) => policy.status === "ACTIVE").length === 1 ? "Verified for certification client" : "Review required"} />
          <Field label="Active immutable" value="Edits require replacement version" />
          <Field label="Rejected reason" value="Required before rejection is recorded" />
          <Field label="Scheduled activation" value="Effective-date conflict check required" />
          <Field label="Superseded history" value="Previous versions remain visible" />
          <Field label="Deletion" value="No UI deletion path" />
        </div>
        <RecordList title="Lifecycle audit" rows={framework.audit} empty="No lifecycle audit records are available." />
      </BillingPanel>
    </div>
  );
}

function PolicyVersionFoundation({ framework, activePolicy }: { framework: BillingPolicyFramework; activePolicy: BillingPolicyFramework["policies"][number] | null }) {
  const versions = framework.audit.filter((item) => String(item.billingPolicyId || item.policyId) === activePolicy?.policyId);
  return (
    <div className="grid gap-4">
      <BillingPanel title="Version Comparison">
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full text-left text-xs">
            <thead className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">
              <tr><th className="py-2 pr-3">Group</th><th className="py-2 pr-3">Changed field</th><th className="py-2 pr-3">Previous</th><th className="py-2 pr-3">New</th><th className="py-2 pr-3">Reason</th></tr>
            </thead>
            <tbody className="divide-y divide-[#D8F3FF] font-bold text-[#0A2540]">
              {["Billing trigger", "Fee model", "Payment terms", "AutoPay", "Reminders", "Service hold", "Risk", "Contacts", "Client visibility"].map((group) => (
                <tr key={group}>
                  <td className="py-3 pr-3">{group}</td>
                  <td className="py-3 pr-3">No replacement delta</td>
                  <td className="py-3 pr-3">Current approved value</td>
                  <td className="py-3 pr-3">No pending replacement</td>
                  <td className="py-3 pr-3">Change reason required for new version</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </BillingPanel>
      <RecordList title="Policy history" rows={versions.length ? versions : framework.audit} empty="No policy history is available." />
    </div>
  );
}

function TemplateManagementFoundation({ framework }: { framework: BillingPolicyFramework }) {
  return (
    <BillingPanel title="Template Management">
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {framework.templates.map((item) => (
          <article key={item.templateId} className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-3">
            <p className="text-sm font-black text-[#0A2540]">{item.templateName}</p>
            <p className="mt-1 text-xs font-bold leading-5 text-[#475569]">{item.description}</p>
            <div className="mt-3 grid gap-2">
              <Field label="Status" value={`${item.status} / version ${item.version}`} />
              <Field label="Managed by" value={item.templateId.includes("GLOBAL") ? "Platform managed" : "Tenant clone ready"} />
              <Field label="Recommended use" value={templateRecommendation(item.templateKey)} />
              <Field label="Last updated" value={formatDate(item.updatedAt)} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {["View Template", "Clone Template", "Create Policy From Template"].map((action) => <span key={action} className="rounded-full border border-[#BFEAFF] bg-white px-3 py-1 text-xs font-black text-[#0A2540]">{action}</span>)}
            </div>
          </article>
        ))}
      </div>
    </BillingPanel>
  );
}

function RiskManagementFoundation({ framework, selectedInvoice }: { framework: BillingPolicyFramework; selectedInvoice: BillingInvoiceRecord }) {
  const risk = framework.riskProfiles.find((profile) => profile.clientId === selectedInvoice.clientId) || framework.riskProfiles[0];
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <BillingPanel title="Billing Risk Review">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Client" value={risk?.clientName || selectedInvoice.clientName || selectedInvoice.clientId} />
          <Field label="Current risk" value={risk?.riskLevel || "LOW"} />
          <Field label="Previous risk" value="Captured in risk history when changed" />
          <Field label="Reliability" value={risk ? `${risk.paymentReliabilityScore}%` : "100%"} />
          <Field label="Late invoices" value={String(risk?.latePaymentCount || 0)} />
          <Field label="Returned payments" value={String(risk?.returnedPaymentCount || 0)} />
          <Field label="Disputes" value={String(risk?.disputeCount || 0)} />
          <Field label="AutoPay failures" value={String(risk?.autopayFailureCount || 0)} />
          <Field label="Service holds" value={risk?.serviceHoldStatus || "None"} />
          <Field label="Approval level" value={risk?.approvalLevel || "Standard"} />
          <Field label="Last review" value={formatDate(risk?.lastBillingReviewAt)} />
          <Field label="Reviewed by" value="Billing Manager" />
        </div>
      </BillingPanel>
      <BillingPanel title="Risk Actions">
        <ActionGrid actions={["Review Risk", "Change Risk Level", "Add Reason", "Approve Recommendation", "Reject Recommendation", "Apply Manual Override", "Remove Manual Override", "View Risk History", "Preview Policy Consequences"]} />
        <p className="mt-4 text-xs font-bold leading-5 text-[#64748B]">Manual overrides require a reason, actor, timestamp, optional expiration, and approval where configured. Client views do not expose opaque scoring.</p>
      </BillingPanel>
    </div>
  );
}

function ServiceHoldFoundation({ framework, selectedInvoice }: { framework: BillingPolicyFramework; selectedInvoice: BillingInvoiceRecord }) {
  const holds = framework.serviceHolds.filter((item) => String(item.clientId) === selectedInvoice.clientId);
  return (
    <div className="grid gap-4">
      <BillingPanel title="Service-Hold Management">
        <ActionGrid actions={["Approve Hold", "Cancel Hold", "Delay Hold", "Change Hold Scope", "Record Exception", "Reinstate Service", "Require Cleared Payment", "Add Manager Note", "View Governing Policy"]} />
      </BillingPanel>
      <RecordList title="Service-hold records" rows={holds} empty="No service-hold records are active for this client." />
    </div>
  );
}

function ReminderManagementFoundation({ framework, selectedInvoice }: { framework: BillingPolicyFramework; selectedInvoice: BillingInvoiceRecord }) {
  const reminders = framework.reminderSchedules.filter((item) => String(item.clientId) === selectedInvoice.clientId);
  return (
    <div className="grid gap-4">
      <BillingPanel title="Reminder Management">
        <ActionGrid actions={["Preview Message", "Reschedule", "Cancel", "Regenerate", "Add Recipient", "Escalate", "Mark As Handled", "View History"]} />
        <p className="mt-4 text-xs font-bold leading-5 text-[#64748B]">Preview reminders remain draft-only in this phase. No email or SMS delivery is triggered by this workspace.</p>
      </BillingPanel>
      <RecordList title="Reminder drafts" rows={reminders} empty="No reminder drafts are scheduled for this client." />
    </div>
  );
}

function BillingAnalyticsFoundation({ framework }: { framework: BillingPolicyFramework }) {
  const metrics = summarizePolicies(framework);
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <BillingPanel title="Revenue and Policy Analytics">
        <Field label="Outstanding billing" value="Derived from Billing Center invoice metrics" />
        <Field label="Collected revenue" value="Preserved from paid invoice state" />
        <Field label="Average days to pay" value="Synthetic dataset: limited trend depth" />
        <Field label="On-time payment rate" value="Preview evidence only" />
      </BillingPanel>
      <BillingPanel title="Policy Distribution">
        <Field label="Policies" value={String(framework.policies.length)} />
        <Field label="Templates" value={String(framework.templates.length)} />
        <Field label="High-risk policies" value={String(metrics.highRisk)} />
        <Field label="AutoPay records" value={String(framework.autopayConsents.length)} />
      </BillingPanel>
      <BillingPanel title="Operational Effectiveness">
        <Field label="Service-hold trend" value={`${framework.serviceHolds.length} Preview records`} />
        <Field label="Reminder effectiveness" value={`${framework.reminderSchedules.length} draft reminders`} />
        <Field label="Dispute trend" value="No meaningful trend from one certification invoice" />
        <Field label="Export" value="Policy summary export foundation available" />
      </BillingPanel>
    </div>
  );
}

function ConfigBlock({ title, values }: { title: string; values: string[] }) {
  return (
    <div className="rounded-2xl border border-[#D8F3FF] bg-[#F8FDFF] p-3">
      <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">{title}</p>
      <p className="mt-2 text-xs font-bold leading-5 text-[#475569]">{values.map(readable).join(", ") || "Configured by policy"}</p>
    </div>
  );
}

function ActionGrid({ actions }: { actions: string[] }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {actions.map((action) => (
        <button key={action} type="button" className="billing-button billing-button-secondary" aria-label={action}>
          {action}
        </button>
      ))}
    </div>
  );
}

function StripePanel({ selected }: { selected: BillingDetail }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <BillingPanel title="Stripe Customer Mapping">
        {selected.stripeCustomer ? Object.entries(selected.stripeCustomer).slice(0, 8).map(([key, value]) => <Field key={key} label={label(key)} value={String(value ?? "")} />) : <p className="text-sm font-bold text-[#475569]">No active Stripe customer mapping yet.</p>}
      </BillingPanel>
      <BillingPanel title="Stripe Invoice / Hosted Payment">
        {selected.stripeInvoice ? Object.entries(selected.stripeInvoice).slice(0, 8).map(([key, value]) => <Field key={key} label={label(key)} value={String(value ?? "")} />) : <p className="text-sm font-bold text-[#475569]">No Stripe hosted payment mapping yet.</p>}
      </BillingPanel>
    </div>
  );
}

function RuleList({ rows, empty }: { rows: string[]; empty: string }) {
  const values = rows.length ? rows : [empty];
  return (
    <div className="flex flex-wrap gap-2">
      {values.map((row) => (
        <span key={row} className="rounded-full border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-1 text-xs font-black text-[#0A2540]">
          {readable(row)}
        </span>
      ))}
    </div>
  );
}

function KeyValues({ value }: { value: Record<string, unknown> }) {
  const entries = Object.entries(value).filter(([, item]) => item != null && item !== "");
  if (!entries.length) return <p className="text-sm font-bold text-[#475569]">Not configured</p>;
  return (
    <div className="grid gap-2">
      {entries.slice(0, 8).map(([key, item]) => (
        <Field key={key} label={label(key)} value={Array.isArray(item) ? item.map((entry) => readable(String(entry))).join(", ") : readable(String(item))} />
      ))}
    </div>
  );
}

function Reports({ reports }: { reports: Awaited<ReturnType<typeof getBillingReports>> | null }) {
  if (!reports) return <p className="text-sm font-bold text-[#475569]">Loading billing reports...</p>;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <BillingPanel title="Issued">
        <Field label="Invoices issued" value={reports.metrics.invoiceCount.toLocaleString()} />
        <Field label="Amount invoiced" value={money(reports.metrics.originalTotalCents)} />
        <Field label="Outstanding" value={money(reports.metrics.remainingBalanceCents)} />
      </BillingPanel>
      <BillingPanel title="Collections">
        <Field label="Paid" value={money(reports.metrics.amountPaidCents)} />
        <Field label="Partial payments" value={reports.metrics.partialPayments.toLocaleString()} />
        <Field label="Payment failures" value={reports.metrics.paymentFailures.toLocaleString()} />
      </BillingPanel>
      <BillingPanel title="Exceptions">
        <Field label="Overdue" value={reports.metrics.overdueInvoices.toLocaleString()} />
        <Field label="Credits" value={money(reports.metrics.creditsCents)} />
        <Field label="Stripe exceptions" value={reports.metrics.stripeExceptions.toLocaleString()} />
      </BillingPanel>
    </div>
  );
}

function RecordList({ title, rows, empty }: { title: string; rows: Array<Record<string, unknown>>; empty: string }) {
  return (
    <BillingPanel title={title}>
      {rows.length === 0 ? <p className="text-sm font-bold text-[#475569]">{empty}</p> : null}
      <div className="grid gap-2">
        {rows.map((row, index) => (
          <div key={String(row.id || row.historyId || row.actionId || index)} className="rounded-2xl border border-[#BFEAFF] bg-[#F8FDFF] p-3">
            <p className="text-sm font-black text-[#0A2540]">{String(row.title || row.actionType || row.paymentStatus || row.receiptNumber || "Billing record")}</p>
            <p className="mt-1 text-xs font-bold text-[#475569]">{String(row.summary || row.reason || row.occurredAt || row.createdAt || "")}</p>
          </div>
        ))}
      </div>
    </BillingPanel>
  );
}

function BillingMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return <div className="bg-white p-4"><p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">{label}</p><p className="mt-1 text-xl font-black text-[#0A2540]">{value}</p><p className="mt-1 text-xs font-bold text-[#64748B]">{note}</p></div>;
}

function BillingFact({ label, value }: { label: string; value: string }) {
  return <div className="rounded-2xl border border-[#BFEAFF] bg-[#F8FDFF] p-3"><p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">{label}</p><p className="mt-1 truncate text-sm font-black text-[#0A2540]">{value}</p></div>;
}

function BillingPanel({ title, children }: { title: string; children: ReactNode }) {
  return <section className="rounded-[1.25rem] border border-[#BFEAFF] bg-white p-4"><h4 className="mb-3 text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">{title}</h4>{children}</section>;
}

function Field({ label, value }: { label: string; value: string }) {
  return <div className="mb-3 last:mb-0"><p className="text-[10px] font-black uppercase tracking-[.14em] text-[#64748B]">{label}</p><p className="break-words text-sm font-black text-[#0A2540]">{value || "Not configured"}</p></div>;
}

function ActionButton({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return <button className="billing-button billing-button-secondary" type="button" disabled={disabled} onClick={onClick}>{children}</button>;
}

function Status({ tone, children }: { tone: "success" | "error"; children: ReactNode }) {
  return <div className={`rounded-2xl border px-4 py-3 text-sm font-black ${tone === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`}>{children}</div>;
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function formatDate(value?: string | null) {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function label(value: string) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function readable(value: unknown) {
  if (value == null || value === "") return "";
  return String(value).replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function summarizePolicies(framework: BillingPolicyFramework) {
  const soon = Date.now() + 1000 * 60 * 60 * 24 * 45;
  return {
    active: framework.policies.filter((policy) => policy.status === "ACTIVE").length,
    draft: framework.policies.filter((policy) => policy.status === "DRAFT").length,
    scheduled: framework.policies.filter((policy) => policy.status === "SCHEDULED").length,
    highRisk: framework.riskProfiles.filter((profile) => profile.riskLevel === "HIGH").length,
    requiresApproval: framework.policies.filter((policy) => String(policy.invoiceApprovalRules.mode || "").toUpperCase().includes("MANUAL")).length,
    expiringSoon: framework.policies.filter((policy) => policy.effectiveTo && new Date(policy.effectiveTo).getTime() <= soon).length,
    serviceHoldsPendingReview: framework.serviceHolds.filter((hold) => String(hold.managerApprovalStatus || hold.approvalStatus || "").toUpperCase().includes("REQUIRED")).length,
  };
}

function sortPolicies(policies: BillingPolicyFramework["policies"], sortMode: string) {
  const result = [...policies];
  result.sort((a, b) => {
    if (sortMode === "policy-name") return a.policyName.localeCompare(b.policyName);
    if (sortMode === "client") return (a.clientName || a.clientId).localeCompare(b.clientName || b.clientId);
    if (sortMode === "effective") return a.effectiveFrom.localeCompare(b.effectiveFrom);
    if (sortMode === "status") return a.status.localeCompare(b.status);
    if (sortMode === "risk") return String(a.riskRules.defaultLevel || "LOW").localeCompare(String(b.riskRules.defaultLevel || "LOW"));
    return b.updatedAt.localeCompare(a.updatedAt);
  });
  return result;
}

function lifecycleRule(status: string) {
  const rules: Record<string, string> = {
    DRAFT: "Editable before approval submission.",
    PENDING_APPROVAL: "Locked from silent edits; approval or rejection required.",
    APPROVED: "May be scheduled or activated when dates do not conflict.",
    SCHEDULED: "Activates on effective date after conflict checks.",
    ACTIVE: "Immutable; replacement creates a new version.",
    SUPERSEDED: "Historical and viewable after replacement.",
    INACTIVE: "Not governing billing; retained for audit.",
    EXPIRED: "Historical, retained, and unavailable for new billing.",
    REJECTED: "Requires a reason and remains in history.",
  };
  return rules[status] || "Controlled lifecycle state.";
}

function templateRecommendation(templateKey: string) {
  const recommendations: Record<string, string> = {
    "collabrecover-standard": "Default active recovery-fee clients",
    "small-business": "Lower-friction small portfolios",
    enterprise: "Large-client approval controls",
    government: "Public-sector billing controls",
    "high-risk": "Manager-reviewed high-risk scenarios",
    "white-label": "Partner-branded billing",
    "strategic-client": "Executive-reviewed accounts",
    "subscription-recovery": "Retainer plus recovery fee",
    custom: "Tenant-specific policy clone",
  };
  return recommendations[templateKey] || "Template-guided client billing";
}

const billingStyles = `
  .billing-center-enterprise { --billing-ink: #0A2540; --billing-aqua: #14B8A6; --billing-line: #BFEAFF; }
  .billing-center-enterprise *, .billing-center-enterprise *::before, .billing-center-enterprise *::after { box-sizing: border-box; min-width: 0; }
  .billing-button { display: inline-flex; min-height: 2.5rem; align-items: center; justify-content: center; gap: .45rem; border-radius: 999px; padding: .55rem .9rem; font-size: .76rem; font-weight: 900; letter-spacing: .06em; text-transform: uppercase; transition: border-color .18s ease, background .18s ease, color .18s ease, opacity .18s ease; }
  .billing-button:focus-visible, .billing-page-button:focus-visible, .billing-tab:focus-visible, .billing-queue-card:focus-visible { outline: 3px solid rgba(20,184,166,.28); outline-offset: 2px; }
  .billing-button:disabled, .billing-page-button:disabled { cursor: not-allowed; opacity: .46; }
  .billing-button-secondary { border: 1px solid #BFEAFF; background: #F4FBFF; color: #0A2540; }
  .billing-button-secondary:hover:not(:disabled) { border-color: #14B8A6; background: #E8FFFB; color: #0F766E; }
  .billing-input { min-height: 2.5rem; width: 100%; border-radius: 1rem; border: 1px solid #BFEAFF; background: #FFFFFF; padding: 0 .75rem; font-size: .78rem; font-weight: 800; color: #0A2540; outline: none; }
  .billing-input:focus { border-color: #14B8A6; box-shadow: 0 0 0 3px rgba(20,184,166,.14); }
  .billing-queue-card { display: block; width: 100%; border-radius: 1.1rem; border: 1px solid #D8F3FF; background: #FFFFFF; padding: .9rem; text-align: left; transition: transform .18s ease, border-color .18s ease, background .18s ease, box-shadow .18s ease; }
  .billing-queue-card:hover, .billing-queue-card.is-active { border-color: #14B8A6; background: #F0FFFC; box-shadow: 0 10px 24px rgba(10,37,64,.08); transform: translateY(-1px); }
  .billing-page-button { border-radius: 999px; border: 1px solid #BFEAFF; background: #F4FBFF; padding: .45rem .75rem; font-size: .72rem; font-weight: 900; text-transform: uppercase; color: #0A2540; }
  .billing-tab { display: inline-flex; flex: 0 0 auto; align-items: center; gap: .45rem; border-radius: 999px; border: 1px solid transparent; padding: .55rem .8rem; font-size: .74rem; font-weight: 900; text-transform: uppercase; color: #475569; }
  .billing-tab.is-active { border-color: #14B8A6; background: #E8FFFB; color: #0F766E; }
`;
